import type { FormValues, ModifiedGoalData, SubGoals } from '../../../types';
import {
	GOAL_FIELD_GROUP,
	CALENDAR_FIELD_NAME,
	DURATION_FIELD_NAME,
	PRIORITY_FIELD_NAME,
	DEFAULT_GOAL_ROW_INDEX_TRACKER,
	VALIDATE_GOAL_FIELD,
	ROW_INDEX_TRACKER,
	READ_ONLY,
	EDITABLE,
	JQL_FIELD_NAME,
} from '../../../ui/constants';

export const toGoalFieldPrefix = (isEditing: boolean) =>
	`${GOAL_FIELD_GROUP}${isEditing ? READ_ONLY : EDITABLE}`;

export const toGoalField = (fieldId: string) => `${toGoalFieldPrefix(true)}${fieldId}`;

export const toGoalDurationFieldName = (fieldId: string, isEditing: boolean) =>
	`${toGoalFieldPrefix(isEditing)}${DURATION_FIELD_NAME}${fieldId}`;

export const toGoalCalendarFieldName = (fieldId: string, isEditing: boolean) =>
	`${toGoalFieldPrefix(isEditing)}${CALENDAR_FIELD_NAME}${fieldId}`;

export const toGoalJqlFieldName = (fieldId: string, isEditing: boolean) =>
	`${toGoalFieldPrefix(isEditing)}${JQL_FIELD_NAME}${fieldId}`;

export const toRowIndexTrackerFieldName = (fieldId: string, isDefaultGoal: boolean) =>
	`${
		isDefaultGoal ? `${DEFAULT_GOAL_ROW_INDEX_TRACKER}${fieldId}` : `${ROW_INDEX_TRACKER}${fieldId}`
	}`;

export const toEditableActionFieldName = (fieldId: string) =>
	`${GOAL_FIELD_GROUP}${VALIDATE_GOAL_FIELD}${fieldId}`;

export const getGoalValuesById = (formValues: FormValues, fieldId: string): ModifiedGoalData => {
	const isEditing = Boolean(formValues[toEditableActionFieldName(fieldId)]);
	const duration = formValues[toGoalDurationFieldName(fieldId, isEditing)];
	const calendarId = formValues[toGoalCalendarFieldName(fieldId, isEditing)]?.value;
	const jqlQuery = formValues[toGoalJqlFieldName(fieldId, isEditing)];
	return {
		jqlQuery,
		duration,
		calendarId,
	};
};

export const toSubGoalPriorityFieldName = (goalId: string, subGoalId: string) =>
	`${GOAL_FIELD_GROUP + PRIORITY_FIELD_NAME + goalId}-${subGoalId}`;

export const toSubGoalCalendarFieldName = (goalId = '', subGoalId: string) =>
	`${GOAL_FIELD_GROUP + CALENDAR_FIELD_NAME + goalId}-${subGoalId}`;

export const toSubGoalDurationFieldName = (goalId = '', subGoalId: string) =>
	`${GOAL_FIELD_GROUP + DURATION_FIELD_NAME + goalId}-${subGoalId}`;

export const getSubGoalValuesById = (
	formValues: FormValues,
	goalId: string,
	subGoals: SubGoals,
	defaultGoal?: boolean,
): ModifiedGoalData => {
	const duration = formValues[toSubGoalDurationFieldName('goal', goalId)];
	const { label: calendarName, value: calendarId } =
		formValues[toSubGoalCalendarFieldName('goal', goalId)];
	const jqlQuery = formValues[toGoalJqlFieldName(goalId, true)];
	const newSubGoals = defaultGoal
		? []
		: subGoals?.map((subgoal) => ({
				...subgoal,
				duration: formValues[toSubGoalDurationFieldName(goalId, subgoal.id)],
				calendarName: formValues[toSubGoalCalendarFieldName(goalId, subgoal.id)]?.label,
				calendarId: formValues[toSubGoalCalendarFieldName(goalId, subgoal.id)]?.value,
				jqlQuery: `priority = "${formValues[
					toSubGoalPriorityFieldName(goalId, subgoal.id)
				].value?.replace(/^"+|"+$/g, '')}"`,
			}));
	return {
		jqlQuery,
		duration,
		calendarId,
		calendarName,
		subGoals: newSubGoals,
	};
};
